import { z } from "zod";

import {
  CanvasModeSchema,
  JsonValueSchema,
  MAX_SCENE_BYTES,
  SerializedExcalidrawElementSchema,
  StudentNumberSchema,
  UuidSchema,
  createSceneSchema,
} from "./domain.js";
import { ErrorCodeSchema } from "./http.js";
import {
  CompletionModeRequestSchema,
  CompletionResetSchema,
  CompletionToggleSchema,
  LessonStateSchema,
  LockRequestSchema,
  StudentCommentsRequestSchema,
  createScopedLessonStateSchema,
} from "./lesson.js";

const byteLength = (value: unknown) =>
  new TextEncoder().encode(JSON.stringify(value)).length;

export const SOCKET_NAMESPACE = "/canvas";
export const SOCKET_TRANSPORT_PATH = "/socket.io";

export const JoinRequestSchema = z
  .object({ classId: UuidSchema, canvasId: UuidSchema })
  .strict();

const TeacherPresenceIdentitySchema = z
  .object({
    connectionId: z.string().min(1),
    role: z.literal("TEACHER"),
    studentNumber: z.null(),
    displayName: z.string().min(1).max(64),
  })
  .strict();

const StudentPresenceIdentitySchema = z
  .object({
    connectionId: z.string().min(1),
    role: z.literal("STUDENT"),
    studentNumber: StudentNumberSchema,
    displayName: z.string().min(1).max(64),
  })
  .strict();

export const PresenceIdentitySchema = z.discriminatedUnion("role", [
  TeacherPresenceIdentitySchema,
  StudentPresenceIdentitySchema,
]);

/**
 * Presence carries more than identity: the roster shows who is idle, who
 * raised a hand, and whose screen actually went behind the cover.
 */
export const PresenceMemberSchema = z
  .object({
    identity: PresenceIdentitySchema,
    state: z.enum(["ACTIVE", "IDLE"]),
    handRaised: z.boolean(),
    /** Null when the hand is down; the moment it was last raised otherwise. */
    raisedAt: z.string().datetime().nullable(),
    /** The client confirmed it drew the cover; a stale tab never reports. */
    covered: z.boolean(),
  })
  .strict();

export const createJoinResultSchema = (
  expectedClassId?: string,
  expectedCanvasId?: string,
  maximumSceneBytes = MAX_SCENE_BYTES,
) =>
  z
    .object({
      classId: expectedClassId ? z.literal(expectedClassId) : UuidSchema,
      canvasId: expectedCanvasId ? z.literal(expectedCanvasId) : UuidSchema,
      epoch: UuidSchema,
      version: z.number().int().min(0),
      scene: createSceneSchema(maximumSceneBytes),
      mode: CanvasModeSchema,
      self: PresenceIdentitySchema,
      collaborators: z.array(PresenceMemberSchema),
      lesson: expectedCanvasId
        ? createScopedLessonStateSchema(expectedCanvasId)
        : LessonStateSchema,
    })
    .strict();

export const JoinResultSchema = createJoinResultSchema();

/** A refused join; the plan's join acknowledgement defines no failure shape. */
export const JoinFailureSchema = z
  .object({ ok: z.literal(false), code: ErrorCodeSchema })
  .strict();

export const ClassJoinRequestSchema = z
  .object({ classId: UuidSchema })
  .strict();

export const createClassJoinAckSchema = (expectedClassId?: string) =>
  z.union([
    z
      .object({
        ok: z.literal(true),
        classId: expectedClassId ? z.literal(expectedClassId) : UuidSchema,
      })
      .strict(),
    JoinFailureSchema,
  ]);

export const ClassJoinAckSchema = createClassJoinAckSchema();

export const CatalogChangedSchema = z.object({ classId: UuidSchema }).strict();

export const createScopedCatalogChangedSchema = (expectedClassId: string) =>
  z.object({ classId: z.literal(expectedClassId) }).strict();

export const createJoinAckSchema = (
  expectedClassId?: string,
  expectedCanvasId?: string,
) =>
  z.union([
    createJoinResultSchema(expectedClassId, expectedCanvasId),
    JoinFailureSchema,
  ]);

const canvasMutationBaseSchema = z
  .object({
    classId: UuidSchema,
    canvasId: UuidSchema,
    epoch: UuidSchema,
    baseVersion: z.number().int().min(0),
    clientId: UuidSchema,
    mutationId: UuidSchema,
    elements: z.array(SerializedExcalidrawElementSchema),
  })
  .strict();

export const createCanvasMutationSchema = (maximumBytes: number) =>
  canvasMutationBaseSchema.superRefine((value, context) => {
    if (byteLength(value) > maximumBytes) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Canvas mutation exceeds ${maximumBytes} bytes`,
      });
    }
  });

export const CanvasMutationSchema = createCanvasMutationSchema(
  10 * 1024 * 1024,
);

export const createMutationAckSchema = (
  expectedClassId?: string,
  expectedCanvasId?: string,
  expectedEpoch?: string,
) =>
  z.discriminatedUnion("ok", [
    z
      .object({
        ok: z.literal(true),
        canvasId: expectedCanvasId ? z.literal(expectedCanvasId) : UuidSchema,
        epoch: expectedEpoch ? z.literal(expectedEpoch) : UuidSchema,
        version: z.number().int().min(0),
      })
      .strict(),
    z
      .object({
        ok: z.literal(false),
        code: ErrorCodeSchema,
        canonical: createJoinResultSchema(
          expectedClassId,
          expectedCanvasId,
        ).optional(),
      })
      .strict(),
  ]);

export const MutationAckSchema = createMutationAckSchema();

const canonicalUpdateSchema = (
  canvasIdSchema: z.ZodType<string>,
  maximumSceneBytes: number,
) =>
  z
    .object({
      canvasId: canvasIdSchema,
      epoch: UuidSchema,
      version: z.number().int().min(0),
      elements: createSceneSchema(maximumSceneBytes),
    })
    .strict();

export const createCanonicalUpdateSchema = (
  maximumSceneBytes = MAX_SCENE_BYTES,
) => canonicalUpdateSchema(UuidSchema, maximumSceneBytes);

export const createScopedCanonicalUpdateSchema = (
  expectedCanvasId: string,
  maximumSceneBytes = MAX_SCENE_BYTES,
) => canonicalUpdateSchema(z.literal(expectedCanvasId), maximumSceneBytes);

export const CanonicalUpdateSchema = createCanonicalUpdateSchema();

export const PresenceUpdateSchema = z
  .object({
    canvasId: UuidSchema,
    state: z.enum(["ACTIVE", "IDLE"]),
    handRaised: z.boolean(),
    /** Null when the hand is down; the moment it was last raised otherwise. */
    raisedAt: z.string().datetime().nullable(),
    covered: z.boolean(),
  })
  .strict();

export const PresenceSnapshotSchema = z
  .object({
    canvasId: UuidSchema,
    collaborators: z.array(PresenceMemberSchema),
  })
  .strict();

export const PointerUpdateSchema = z
  .object({
    canvasId: UuidSchema,
    pointer: z.object({ x: z.number(), y: z.number() }).strict(),
    button: z.enum(["down", "up"]),
    selectedElementIds: z.record(z.boolean()),
  })
  .strict();

export const ServerPointerUpdateSchema = PointerUpdateSchema.extend({
  identity: PresenceIdentitySchema,
}).strict();

export const CanvasRevokedSchema = z
  .object({
    canvasId: UuidSchema,
    code: ErrorCodeSchema,
    reason: z.string().min(1),
  })
  .strict();

export const SessionExpiringSchema = z
  .object({ expiresAt: z.string().datetime() })
  .strict();

export const SocketAuthSchema = z
  .object({ csrfToken: z.string().min(32) })
  .strict();

/**
 * Asks which scene hash the server holds for the version the editor believes
 * it is at. Old editors never send it, so it can ship without a lockstep
 * upgrade. The hash is hex SHA-256 of `canonicalJson(orderScene(scene))`.
 */
export const CanvasVerifyRequestSchema = z
  .object({ canvasId: UuidSchema })
  .strict();

export const CanvasVerifyAckSchema = z.discriminatedUnion("ok", [
  z
    .object({
      ok: z.literal(true),
      canvasId: UuidSchema,
      epoch: UuidSchema,
      version: z.number().int().min(0),
      sceneHash: z.string().regex(/^[0-9a-f]{64}$/),
    })
    .strict(),
  z.object({ ok: z.literal(false), code: ErrorCodeSchema }).strict(),
]);

/** Comfortably above any scene `MAX_SCENE_BYTES` can hold. */
const MAX_REPAIR_MANIFEST_ENTRIES = 100_000;

/**
 * After a verified hash mismatch, the editor lists every element it believes
 * the server holds as `[id, elementDigest]`, and the server answers with only
 * the elements that differ instead of the editor rejoining for the whole
 * scene. An old server never answers it; the editor then rejoins as before.
 */
export const CanvasRepairRequestSchema = z
  .object({
    canvasId: UuidSchema,
    manifest: z
      .array(z.tuple([z.string().min(1).max(200), z.string().min(1).max(32)]))
      .max(MAX_REPAIR_MANIFEST_ENTRIES),
  })
  .strict();

export const createCanvasRepairAckSchema = (
  maximumSceneBytes = MAX_SCENE_BYTES,
) =>
  z.discriminatedUnion("ok", [
    z
      .object({
        ok: z.literal(true),
        canvasId: UuidSchema,
        epoch: UuidSchema,
        version: z.number().int().min(0),
        /** The server's copy of every element that is missing from the
         * manifest or whose digest differs. */
        elements: createSceneSchema(maximumSceneBytes),
        /** Manifest ids the server does not hold at all. */
        missing: z.array(z.string().min(1).max(200)),
      })
      .strict(),
    z.object({ ok: z.literal(false), code: ErrorCodeSchema }).strict(),
  ]);

export const CanvasRepairAckSchema = createCanvasRepairAckSchema();

/**
 * Editor-side sync events an operator needs to trace a report after the
 * fact (refused edits snapping back, resyncs, divergence). Identifiers and
 * counts only; never drawing content.
 */
export const CanvasDiagnosticSchema = z
  .object({
    canvasId: UuidSchema,
    kind: z.string().regex(/^[a-z_]{1,40}$/),
    detail: z
      .record(
        z.union([
          z.string().max(200),
          z.number(),
          z.boolean(),
          z.null(),
          z.array(z.string().max(80)).max(50),
        ]),
      )
      .refine((value) => Object.keys(value).length <= 20)
      .optional(),
  })
  .strict();

export const EphemeralJsonSchema = JsonValueSchema;

export const CanvasClientEventContracts = {
  "canvas:join": {
    payload: JoinRequestSchema,
    acknowledgement: (request: JoinRequest) =>
      createJoinAckSchema(request.classId, request.canvasId),
    capability: "CANVAS_READ",
  },
  "canvas:mutation": {
    payload: CanvasMutationSchema,
    acknowledgement: (request: CanvasMutation) =>
      createMutationAckSchema(request.classId, request.canvasId, request.epoch),
    capability: "CANVAS_EDIT",
  },
  "presence:update": {
    payload: PresenceUpdateSchema,
    acknowledgement: null,
    capability: null,
  },
  "pointer:update": {
    payload: PointerUpdateSchema,
    acknowledgement: null,
    capability: null,
  },
  "class:join": {
    payload: ClassJoinRequestSchema,
    acknowledgement: (request: ClassJoinRequest) =>
      createClassJoinAckSchema(request.classId),
    capability: "CLASS_READ",
  },
  "lesson:lock": {
    payload: LockRequestSchema,
    acknowledgement: null,
    capability: "CANVAS_MANAGE",
  },
  "lesson:completionMode": {
    payload: CompletionModeRequestSchema,
    acknowledgement: null,
    capability: "CANVAS_MANAGE",
  },
  "lesson:reset": {
    payload: CompletionResetSchema,
    acknowledgement: null,
    capability: "CANVAS_MANAGE",
  },
  "lesson:studentComments": {
    payload: StudentCommentsRequestSchema,
    acknowledgement: null,
    capability: "CANVAS_MANAGE",
  },
  "lesson:completion": {
    payload: CompletionToggleSchema,
    acknowledgement: null,
    capability: null,
  },
  "canvas:verify": {
    payload: CanvasVerifyRequestSchema,
    acknowledgement: () => CanvasVerifyAckSchema,
    capability: "CANVAS_READ",
  },
  "canvas:repair": {
    payload: CanvasRepairRequestSchema,
    acknowledgement: () => CanvasRepairAckSchema,
    capability: "CANVAS_READ",
  },
  "canvas:diagnostic": {
    payload: CanvasDiagnosticSchema,
    acknowledgement: null,
    capability: null,
  },
} as const;

export const SocketServerEventContracts = {
  "canvas:canonical": (canvasId: string) =>
    createScopedCanonicalUpdateSchema(canvasId),
  "presence:snapshot": PresenceSnapshotSchema,
  "pointer:update": ServerPointerUpdateSchema,
  "canvas:revoked": CanvasRevokedSchema,
  "session:expiring": SessionExpiringSchema,
  "catalog:changed": (classId: string) =>
    createScopedCatalogChangedSchema(classId),
  "lesson:state": (canvasId: string) => createScopedLessonStateSchema(canvasId),
} as const;

export const SOCKET_EVENT_CAPABILITIES = Object.fromEntries(
  Object.entries(CanvasClientEventContracts)
    .filter(([, contract]) => Boolean(contract.capability))
    .map(([name, contract]) => [name, contract.capability]),
) as Readonly<
  Partial<
    Record<
      keyof typeof CanvasClientEventContracts,
      "CANVAS_READ" | "CANVAS_EDIT" | "CANVAS_MANAGE" | "CLASS_READ"
    >
  >
>;

export type JoinRequest = z.infer<typeof JoinRequestSchema>;
export type JoinResult = z.infer<typeof JoinResultSchema>;
export type JoinFailure = z.infer<typeof JoinFailureSchema>;
export type JoinAck = JoinResult | JoinFailure;
export type PresenceIdentity = z.infer<typeof PresenceIdentitySchema>;
export type PresenceMember = z.infer<typeof PresenceMemberSchema>;
export type CanvasMutation = z.infer<typeof CanvasMutationSchema>;
export type MutationAck = z.infer<typeof MutationAckSchema>;
export type CanonicalUpdate = z.infer<typeof CanonicalUpdateSchema>;
export type CanvasVerifyRequest = z.infer<typeof CanvasVerifyRequestSchema>;
export type CanvasVerifyAck = z.infer<typeof CanvasVerifyAckSchema>;
export type CanvasRepairRequest = z.infer<typeof CanvasRepairRequestSchema>;
export type CanvasRepairAck = z.infer<typeof CanvasRepairAckSchema>;
export type CanvasDiagnostic = z.infer<typeof CanvasDiagnosticSchema>;
export type PresenceSnapshot = z.infer<typeof PresenceSnapshotSchema>;
export type PresenceUpdate = z.infer<typeof PresenceUpdateSchema>;
export type PointerUpdate = z.infer<typeof PointerUpdateSchema>;
export type ServerPointerUpdate = z.infer<typeof ServerPointerUpdateSchema>;
export type CanvasRevoked = z.infer<typeof CanvasRevokedSchema>;
export type SessionExpiring = z.infer<typeof SessionExpiringSchema>;
export type SocketAuth = z.infer<typeof SocketAuthSchema>;
export type ClassJoinRequest = z.infer<typeof ClassJoinRequestSchema>;
export type ClassJoinAck = z.infer<typeof ClassJoinAckSchema>;
export type CatalogChanged = z.infer<typeof CatalogChangedSchema>;
